import React, { createContext, useContext, useState } from "react";
import { createPortal } from "react-dom";

interface BreadcrumbSlotContextValue {
  slot: HTMLElement | null;
  setSlot: (node: HTMLElement | null) => void;
}

const BreadcrumbSlotContext = createContext<BreadcrumbSlotContextValue | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the DOM node of the breadcrumb-row action slot so any routed page can
 * portal its own header actions into it. Wraps the breadcrumb row and the page
 * switch together so both share the same slot.
 */
export const BreadcrumbSlotProvider: React.FC<ProviderProps> = ({ children }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  return <BreadcrumbSlotContext.Provider value={{ slot, setSlot }}>{children}</BreadcrumbSlotContext.Provider>;
};

/** Callback ref for the breadcrumb row to register its right-aligned slot node. */
export const useBreadcrumbSlotRef = (): ((node: HTMLElement | null) => void) => {
  const ctx = useContext(BreadcrumbSlotContext);
  return ctx?.setSlot ?? (() => {});
};

interface BreadcrumbActionsProps {
  children: React.ReactNode;
}

/**
 * Teleports its children into the breadcrumb-row slot. Stays mounted in the
 * caller's React tree (so it keeps access to that page's context/providers),
 * while rendering visually next to the page title — only on pages that use it.
 */
export const BreadcrumbActions: React.FC<BreadcrumbActionsProps> = ({ children }) => {
  const ctx = useContext(BreadcrumbSlotContext);

  if (!ctx?.slot) return null;

  return createPortal(children, ctx.slot);
};
